import { type Ref, ref, toValue, watch } from 'vue'

import { makeQueryStringForReturnAds, normalizeReturnAds } from '@/lib/adapters/return-ads.adapter'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import type { ReturnAdListItemType, ReturnAdsFilterType } from '@/lib/types/return-ads.types'
import { useErrorStore } from '@/stores/error'
import { useUserStore } from '@/stores/user'

export const useGetReturnAds = (filterRef: Ref<ReturnAdsFilterType>) => {
  const data = ref<ReturnAdListItemType[] | undefined>()
  const totalPages = ref<number>(0)
  const links = ref<{ first?: string; last?: string; prev?: string; next?: string }>({})
  const userStore = useUserStore()
  const isLoading = ref(true)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchReturnAds = async (query: string) => {
    try {
      data.value = undefined
      isLoading.value = true
      error.value = null

      const response = await request.get(`/v2/return-ads?${query}`)
      data.value = normalizeReturnAds(response.data.data)
      totalPages.value = response.data.meta.last_page
      links.value = response.data.links
    } catch (e) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      isLoading.value = false
    } finally {
      isLoading.value = false
    }
  }

  watch(
    filterRef,
    async () => {
      const filter = toValue(filterRef)
      const query = makeQueryStringForReturnAds(filter, userStore.id)

      if (query) {
        await fetchReturnAds(query)
      }
    },
    { deep: true, immediate: true },
  )

  return {
    data,
    links,
    totalPages,
    isLoading,
    error,
  }
}
